/**
 * Editor snapping. Snapping is an authoring aid, not a document property: a saved map records the
 * snapped values and nothing about the grid that produced them, so turning snapping on or off
 * never changes a map that is already drawn.
 */

import type { Vec2 } from './types.ts';

/** One grid cell at the play grid's base spacing, which is what the editor draws. */
export const SNAP_GRID = 100;
export const SNAP_ANGLE_DEG = 15;
export const SNAP_SIZE_STEP = 0.1;

export function snapValue(value: number, step: number): number {
  if (!(step > 0)) {
    return value;
  }

  return Math.round(value / step) * step;
}

export function snapPoint(point: Vec2, step: number): Vec2 {
  return { x: snapValue(point.x, step), y: snapValue(point.y, step) };
}

/** Snaps a delta rather than a position, so a dragged group keeps its internal spacing. */
export function snapDelta(delta: Vec2, step: number): Vec2 {
  return snapPoint(delta, step);
}

/** Wraps into [0, 360) after snapping so rotations stay in the range the inspector shows. */
export function snapAngleDeg(degrees: number, step = SNAP_ANGLE_DEG): number {
  const snapped = snapValue(degrees, step);
  return ((snapped % 360) + 360) % 360;
}

/** Snaps a signed radian delta to the same step as `snapAngleDeg`. */
export function snapAngleRad(radians: number, stepDeg = SNAP_ANGLE_DEG): number {
  const step = (stepDeg * Math.PI) / 180;
  return snapValue(radians, step);
}
